<template>
  <div class="page-header-links">
    <template v-if="showContact">
      <PageCountrySelect :country="country" @change="onChangeCountry"></PageCountrySelect>
      <div class="header-link-divider">|</div>
    </template>
    <div v-if="showSearchIcon" class="page-header-search">
      <a :href="searchUrl" class="header-link" aria-label="Search" title="Search" @click.prevent="toggleSearch"><span class="af af-search"></span></a>
    </div>
    <div v-if="showSearchIcon" class="header-link-divider">|</div>
    <template v-if="showContact">
      <AboutLink class="header-link" :about-url="aboutUrl"></AboutLink>
      <div class="header-link-divider">|</div>
      <ChatLink class="header-link" :chat-url="chatUrl" @chat="chat"></ChatLink>
      <div class="header-link-divider">|</div>
      <PhoneLink class="header-link"></PhoneLink>
      <div class="header-link-divider">|</div>
    </template>
    <div class="page-header-account">
      <a :href="accountUrl" class="header-link" aria-label="Account" title="Account"><i class="af af-account"></i></a>
    </div>
    <div class="header-link-divider">|</div>
    <div class="page-header-favorites">
      <a :href="favoritesUrl" class="header-link" aria-label="Favorites" title="Favorites">
        <i class="af af-heart"></i>
        <span v-if="mounted && favoritesCount" class="page-header-favorites-count">{{ favoritesCount }}</span>
      </a>
    </div>
    <div class="header-link-divider">|</div>
    <div v-click-outside="closeCart" class="page-header-cart" @click="delayShow = true">
      <a
        :href="cartUrl"
        class="header-link"
        :class="{ 'header-link-disabled': !enableMiniCart }"
        aria-label="Cart"
        title="Cart"
        @click.prevent="toggleCart"
      >
        <i class="af af-cart"></i>
        <span class="page-header-cart-count" :style="{ visibility: cartCount ? 'visible' : 'hidden' }">{{ cartCount }}</span>
      </a>
      <transition name="cart-fade">
        <div v-if="showCart" class="mini-cart-container" :class="{ 'mini-cart-menu-right': showMenuIcon }">
          <suspense>
            <MiniCart :cart="cart" @checkout="checkout" @closeMiniCart="closeCart" :isShown="showCart" :cartFetched="cartFetched"></MiniCart>
          </suspense>
        </div>
      </transition>
    </div>
    <div v-if="showMenuIcon" class="page-header-menu">
      <a v-if="!showMenuClose" class="header-link" aria-label="Open Menu" tabindex="0" @click.prevent="openMenu"><i class="af af-menu"></i></a>
      <a v-else class="header-link" aria-label="Close Menu" tabindex="0" @click.prevent="closeMenu"><i class="af af-close"></i></a>
    </div>
  </div>
</template>
<script>
import ClickOutside from '@/pixel/utils/ClickOutside';

import AboutLink from './AboutLink.vue';
import ChatLink from './ChatLink.vue';
import PhoneLink from './PhoneLink.vue';
import PageCountrySelect from './PageCountrySelect.vue';

import Tracking from '@/components/commons/Tracking.js';
import { defineAsyncComponent } from 'vue';
import useLifecycleHook from '@/compositions/useLifecycleHook';

export default {
  components: {
    PageCountrySelect,
    AboutLink,
    ChatLink,
    PhoneLink,
    MiniCart: defineAsyncComponent(() => import('@/components/cart/MiniCart.vue')),
  },

  directives: {
    ClickOutside,
  },

  props: [
    'showCart',
    'showContact',
    'showMenuClose',
    'showMenuIcon',
    'showSearchIcon',
    'enableMiniCart',
    'country',
    'searchUrl',
    'aboutUrl',
    'chatUrl',
    'accountUrl',
    'favoritesUrl',
    'cartUrl',
    'cart',
    'cartCount',
    'favoritesCount',
    'cartFetched',
  ],

  setup() {
    const { mounted } = useLifecycleHook();

    return {
      mounted
    };
  },

  methods: {
    openMenu() {
      this.$emit('openMenu');
    },

    closeMenu() {
      this.$emit('closeMenu');
    },

    toggleCart() {
      if (this.enableMiniCart) {
        Tracking.track('Mini Cart Open');
        this.$emit('toggleCart');
      }
    },

    closeCart() {
      if (this.showCart) {
        this.$emit('closeCart');
      }
    },

    toggleSearch() {
      this.$emit('toggleSearch');
    },

    chat() {
      this.$emit('chat');
    },

    checkout() {
      this.$emit('checkout');
    },

    onChangeCountry(country) {
      this.$emit('changeCountry', country);
    },
  },
};
</script>

<style lang="postcss">
@import '@/pixel/style/variables';

.page-header-links {
  display: flex;
  align-items: center;
  flex-grow: 1;
  justify-content: flex-end;

  a,
  a:visited,
  a:focus,
  a:active {
    color: var(--font-color);
  }

  .page-header-cart,
  .page-header-favorites {
    user-select: none;
    position: relative;
  }

  & > * {
    display: none;
    margin-left: 30px;

    @media (min-width: $tablet-breakpoint) {
      display: block;
      margin-left: 0px;
    }

    @media (max-width: calc($small-breakpoint - 1px)) {
      margin-left: 25px;
    }
  }

  .header-link-disabled {
    cursor: default;
  }

  .header-link-divider {
    margin-left: 15px;
    margin-right: 15px;
  }

  .header-link-divider,
  .header-link {
    touch-action: manipulation;
    font-size: var(--font-size);

    @media (min-width: $tablet-breakpoint) {
      font-size: var(--font-size-14);
    }
  }

  .page-header-search,
  .page-header-cart,
  .page-header-menu {
    display: block;
  }

  .page-header-menu {
    @media (min-width: $tablet-breakpoint) {
      display: none;
    }
  }

  .page-header-cart-count,
  .page-header-favorites-count {
    top: 0px;
    right: -10px;
    width: 14px;
    height: 14px;
    line-height: 16px;
    border-radius: 50%;
    position: absolute;
    font-family: var(--font-bold);
    font-size: 0.5em;
    color: var(--white);
    background-color: var(--accent-color);
    text-align: center;
  }

  .mini-cart-container {
    opacity: 1;
    right: 0;
    transition: all 150ms ease-out;
    top: 0;
    position: fixed;
    z-index: 9;

    @media (max-width: calc($mobile-breakpoint - 1px)) {
      left: 0px;
      right: 0px;
      width: 100%;
      .mini-cart-content {
        width: 100%;
      }
    }

    &.cart-fade-enter-from,
    &.cart-fade-leave-to {
      opacity: 0;
      top: 30px;
    }

    &.cart-fade-enter-from,
    &.cart-fade-leave-to {
      opacity: 0;
      top: 0px;
      right: -40px;
      @media (max-width: 760px) {
        top: 40px;
        right: 0;
      }
    }
  }

  .count-change-enter-from,
  .count-change-leave-to {
    transform: scale(1.1);
  }
}
</style>
